import { CheckCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  EducationType,
  EnrollmentStatus,
} from "../../types/client-education-detail.types";
import type {
  EducationDetailDto,
  EnrollmentDto,
} from "../../types/client-education-detail.types";
import styles from "./DetailSidebar.module.scss";

type MilestoneKey = "enrolled" | "contentOrAttendance" | "exam" | "survey" | "certificate";

interface Milestone {
  key: MilestoneKey;
  label: string;
  state: "done" | "active" | "upcoming";
  sectionId: string;
}

function buildMilestones(
  education: EducationDetailDto,
  enrollment: EnrollmentDto,
  t: TFunction,
): Milestone[] {
  const st = enrollment.status;
  const isContent =
    education.type === EducationType.UNIT ||
    education.type === EducationType.NONUNIT;
  const middleLabel = isContent
    ? t("milestoneTimeline.content")
    : t("milestoneTimeline.attendance");
  const middleSection = isContent
    ? "content"
    : education.type === EducationType.ONLINE
      ? "virtualClassrooms"
      : "scheduling";

  const progress = enrollment.progressPercent ?? 0;

  const m: Milestone[] = [
    {
      key: "enrolled",
      label: t("milestoneTimeline.enrolled"),
      state: "done",
      sectionId: "hero",
    },
    {
      key: "contentOrAttendance",
      label: middleLabel,
      state:
        st >= EnrollmentStatus.CONTENT_COMPLETED
          ? "done"
          : progress > 0 || st === EnrollmentStatus.ENROLLED
            ? "active"
            : "upcoming",
      sectionId: middleSection,
    },
  ];

  if (education.exam?.id) {
    m.push({
      key: "exam",
      label: t("milestoneTimeline.exam"),
      state:
        st >= EnrollmentStatus.EXAM_COMPLETED
          ? "done"
          : st === EnrollmentStatus.CONTENT_COMPLETED ||
              st === EnrollmentStatus.EXAM_STARTED ||
              st === EnrollmentStatus.EXAM_FAILED
            ? "active"
            : "upcoming",
      sectionId: "exam",
    });
  }

  if (education.survey) {
    m.push({
      key: "survey",
      label: t("milestoneTimeline.survey"),
      state:
        st === EnrollmentStatus.COMPLETED
          ? "done"
          : st === EnrollmentStatus.EXAM_COMPLETED ||
              st === EnrollmentStatus.SURVEY_STARTED
            ? "active"
            : "upcoming",
      sectionId: "survey",
    });
  }

  if (education.certificateId) {
    m.push({
      key: "certificate",
      label: t("milestoneTimeline.certificate"),
      state: st === EnrollmentStatus.COMPLETED ? "active" : "upcoming",
      sectionId: "certificate",
    });
  }

  return m;
}

export function MilestoneTimeline(props: {
  education: EducationDetailDto;
  enrollment: EnrollmentDto;
  onJump: (sectionId: string) => void;
}) {
  const { t } = useTranslation();
  const items = buildMilestones(props.education, props.enrollment, t);
  return (
    <ol className={styles.milestones}>
      {items.map((m) => (
        <li key={m.key} className={styles[`milestone-${m.state}`]}>
          <button
            type="button"
            onClick={() => props.onJump(m.sectionId)}
            className={styles.milestoneBtn}
          >
            <span className={styles.milestoneIcon}>
              {m.state === "done" ? <CheckCircleFilled /> : <span className={styles.dot} />}
            </span>
            <span className={styles.milestoneLabel}>{m.label}</span>
          </button>
        </li>
      ))}
    </ol>
  );
}
